(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  const animated = new Set();
  const readings = new Set();
  const heroes = new Set();
  const journeys = new Set();
  const voiceScenes = new Set();
  const measureJourney = (journey) => {
    const stage = journey.querySelector('.ia-journey-stage');
    const panels = [...journey.querySelectorAll('.ia-journey-panel')];
    const enhance = !reduced.matches;
    journey.classList.toggle('ia-journey-enhanced', enhance);
    if (!enhance) {
      journey.style.removeProperty('height');
      journey.querySelector('.ia-journey-track').style.removeProperty('transform');
      return;
    }
    const tooTall = panels.some((panel) => {
      const copy = panel.querySelector('.ia-journey-copy');
      if (!copy) return false;
      const style = getComputedStyle(panel);
      const available = panel.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      return copy.scrollHeight > available - 8;
    });
    if (tooTall) {
      journey.classList.remove('ia-journey-enhanced');
      journey.style.removeProperty('height');
      journey.querySelector('.ia-journey-track').style.removeProperty('transform');
      return;
    }
    const portrait = journey.querySelector('.ia-journey-mobile-portrait');
    const before = portrait && getComputedStyle(portrait).display !== 'none' ? portrait.offsetTop + portrait.offsetHeight + parseFloat(getComputedStyle(portrait).marginBottom || 0) : 0;
    journey.style.height = `${before + stage.clientHeight * (1 + (panels.length - 1) * .8)}px`;
  };
  let frame = false;
  let observer;
  const words = (element, className) => {
    if (element.dataset.iaSplit) return;
    element.dataset.iaSplit = 'true';
    element.setAttribute('aria-label', element.textContent.trim().replace(/\s+/g, ' '));
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let i = 0;
    nodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).filter(Boolean).forEach((part) => {
        if (/^\s+$/.test(part)) fragment.append(document.createTextNode(part));
        else {
          const word = document.createElement('span');
          word.className = className;
          word.textContent = part;
          word.setAttribute('aria-hidden', 'true');
          word.style.setProperty('--ia-word-i', Math.min(i++, 13));
          fragment.append(word);
        }
      });
      node.replaceWith(fragment);
    });
  };
  const show = (element) => {
    element.classList.add('ia-in-view');
    animated.delete(element);
    observer?.unobserve(element);
  };
  const update = () => {
    frame = false;
    document.querySelectorAll('[data-ia-header]').forEach((header) => header.classList.toggle('ia-scrolled', window.scrollY > 40));
    if (reduced.matches) return;
    const viewport = window.innerHeight;
    voiceScenes.forEach((scene) => { if (scene.section.isConnected) scene.update(); else voiceScenes.delete(scene); });
    journeys.forEach((journey) => {
      if (!journey.isConnected) { journeys.delete(journey); return; }
      if (!journey.classList.contains('ia-journey-enhanced')) return;
      const journeyRect = journey.getBoundingClientRect();
      if (journeyRect.bottom < 0 || journeyRect.top > viewport * 1.2) return;
      const stage = journey.querySelector('.ia-journey-stage');
      const track = journey.querySelector('.ia-journey-track');
      const view = journey.querySelector('.ia-journey-viewport');
      const portrait = journey.querySelector('.ia-journey-mobile-portrait');
      const before = portrait && getComputedStyle(portrait).display !== 'none' ? portrait.offsetTop + portrait.offsetHeight + parseFloat(getComputedStyle(portrait).marginBottom || 0) : 0;
      const run = journey.offsetHeight - stage.offsetHeight - before;
      const pinTop = parseFloat(getComputedStyle(stage).top) || 0;
      const progress = Math.max(0, Math.min(1, (pinTop - journeyRect.top - before) / Math.max(1, run)));
      track.style.transform = `translate3d(${-progress * (track.scrollWidth - view.clientWidth)}px,0,0)`;
      journey.querySelector('.ia-journey-progress span').style.transform = `scaleX(${progress})`;
    });
    heroes.forEach((hero) => {
      if (!hero.isConnected) { heroes.delete(hero); return; }
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > viewport * 1.2) return;
      const distance = Math.max(viewport * .8, Math.min(rect.height * .9, viewport * 1.45));
      const progress = Math.max(0, Math.min(1, -rect.top / distance));
      hero.style.setProperty('--ia-scroll', progress.toFixed(4));
    });
    animated.forEach((element) => {
      if (!element.isConnected) { animated.delete(element); return; }
      if (element.getBoundingClientRect().top < viewport * .96) show(element);
    });
    readings.forEach((element) => {
      if (!element.isConnected) { readings.delete(element); return; }
      const rect = element.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport * 1.2) return;
      const readingRun = element.closest('.ia-statement') ? viewport * .85 + rect.height * .35 : viewport * .53 + rect.height * .25;
      const progress = Math.max(0, Math.min(1, (viewport * .88 - rect.top) / readingRun));
      const list = element.querySelectorAll('.ia-read-word');
      list.forEach((word, i) => {
        const active = Math.max(0, Math.min(1, progress * (list.length + 3) - i));
        if (element.closest('.ia-statement')) {
          word.style.opacity = '1';
          word.style.setProperty('--ia-read-color', `rgb(${Math.round(138 - 47 * active)},${Math.round(115 - 57 * active)},${Math.round(96 - 55 * active)})`);
        } else word.style.opacity = String(.3 + active * .7);
      });
    });
  };
  const requestUpdate = () => {
    if (!frame) { frame = true; requestAnimationFrame(update); }
  };
  const init = (scope = document) => {
    scope.querySelectorAll('[data-ia-journey]').forEach((journey) => {
      if (journeys.has(journey)) return;
      journeys.add(journey);
      measureJourney(journey);
      if ('ResizeObserver' in window) {
        const resize = new ResizeObserver(() => { measureJourney(journey); requestUpdate(); });
        resize.observe(journey.querySelector('.ia-journey-stage'));
        journey.querySelectorAll('.ia-journey-copy').forEach(copy => resize.observe(copy));
      }
    });
    scope.querySelectorAll('[data-ia-nav-toggle]').forEach((button) => {
      if (initialized.has(button)) return;
      initialized.add(button);
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      const close = () => {
        button.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        panel.querySelectorAll('[data-ia-nav-toggle]').forEach((child) => {
          child.setAttribute('aria-expanded', 'false');
          document.getElementById(child.getAttribute('aria-controls')).hidden = true;
        });
      };
      close();
      button.hidden = false;
      button.addEventListener('click', () => {
        const opening = button.getAttribute('aria-expanded') !== 'true';
        if (!opening) { close(); return; }
        const list = button.closest('.ia-nav-list');
        list.querySelectorAll(':scope > .ia-nav-item > .ia-nav-row > [data-ia-nav-toggle]').forEach((sibling) => {
          if (sibling !== button && sibling.getAttribute('aria-expanded') === 'true') sibling.click();
        });
        button.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
      });
      button.closest('.ia-nav-item').addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          close();
          button.focus();
        }
      });
    });
    scope.querySelectorAll('[data-ia-menu]').forEach((menu) => {
      if (initialized.has(menu)) return;
      initialized.add(menu);
      const summary = menu.querySelector('summary');
      summary.setAttribute('aria-expanded', String(menu.open));
      menu.addEventListener('toggle', () => {
        document.body.classList.toggle('ia-menu-open', menu.open);
        summary.setAttribute('aria-expanded', String(menu.open));
        if (menu.open) {
          const bottom = menu.closest('[data-ia-header]').getBoundingClientRect().bottom;
          menu.querySelector('.ia-menu-panel').style.top = `${Math.max(12, bottom + 8)}px`;
        }
      });
      menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => { menu.open = false; }));
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { menu.open = false; summary.focus(); }
        if (event.key === 'Tab' && menu.open) {
          const focusable = [summary, ...menu.querySelectorAll('a, button, input:not([type="hidden"]), select, textarea')].filter((element) => !element.closest('[hidden]') && !element.disabled);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      });
    });
    scope.querySelectorAll('.ia-topic').forEach((topic) => {
      if (initialized.has(topic)) return;
      initialized.add(topic);
      topic.addEventListener('toggle', () => {
        if (topic.open) topic.parentElement.querySelectorAll('.ia-topic').forEach((other) => { if (other !== topic) other.open = false; });
      });
    });
    scope.querySelectorAll('[data-ia-voices]').forEach((section) => {
      if (initialized.has(section)) return;
      initialized.add(section);
      const voices = [...section.querySelectorAll('[data-ia-voice]')];
      if (voices.length < 2) return;
      const pin = section.querySelector('.ia-voices-pin');
      const counter = section.querySelector('[data-ia-counter]');
      const controls = section.querySelector('.ia-voices-controls');
      let current = 0;
      let run = 0;
      section.classList.add('ia-voices-scroll');
      controls.hidden = false;
      const display = (index) => {
        current = index;
        voices.forEach((voice, i) => {
          voice.hidden = false;
          voice.classList.toggle('ia-voice-current', i === index);
          voice.inert = i !== index;
          voice.setAttribute('aria-hidden', String(i !== index));
        });
        counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(voices.length).padStart(2, '0')}`;
      };
      const measure = () => {
        const expanded = !!section.querySelector('details[open]');
        const fits = pin.offsetHeight <= window.innerHeight - 24;
        const pinned = !reduced.matches && !expanded && fits;
        section.classList.toggle('ia-voices-pinned', pinned);
        run = Math.max(210, document.documentElement.clientHeight * .28) * (voices.length - 1);
        if (pinned) section.style.height = `${pin.offsetHeight + run}px`;
        else section.style.removeProperty('height');
      };
      const scene = { section, measure, update: () => {
        if (!section.classList.contains('ia-voices-pinned')) return;
        const progress = Math.max(0, Math.min(1, (12 - section.getBoundingClientRect().top) / Math.max(1, run)));
        const index = Math.round(progress * (voices.length - 1));
        if (index !== current && !section.querySelector('details[open]')) display(index);
      }};
      const go = (direction) => {
        const index = (current + direction + voices.length) % voices.length;
        voices.forEach((voice) => voice.querySelectorAll('details').forEach((detail) => { detail.open = false; }));
        display(index);
        measure();
        if (section.classList.contains('ia-voices-pinned')) {
          const start = window.scrollY + section.getBoundingClientRect().top - 12;
          window.scrollTo({ top: start + run * index / (voices.length - 1), behavior: 'instant' });
        }
      };
      section.querySelector('[data-ia-previous]').addEventListener('click', () => go(-1));
      section.querySelector('[data-ia-next]').addEventListener('click', () => go(1));
      section.querySelectorAll('details').forEach((detail) => detail.addEventListener('toggle', measure));
      display(0);
      voiceScenes.add(scene);
      measure();
      if ('ResizeObserver' in window) new ResizeObserver(measure).observe(pin);
      document.fonts?.ready.then(measure);
    });
    scope.querySelectorAll('[data-ia-count]').forEach((element) => {
      if (initialized.has(element)) return;
      initialized.add(element);
      const final = element.textContent.trim();
      element.setAttribute('aria-label', final);
      const visual = document.createElement('span');
      visual.setAttribute('aria-hidden', 'true');
      visual.textContent = final;
      element.replaceChildren(visual);
      if (reduced.matches || !('IntersectionObserver' in window)) return;
      const countObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        countObserver.disconnect();
        const started = performance.now();
        const tick = (now) => {
          const progress = Math.min(1, (now - started) / 1400);
          const eased = 1 - Math.pow(1 - progress, 3);
          visual.textContent = progress === 1 || reduced.matches ? final : final.replace(/\d+(?:[,.]\d+)?/g, (number) => {
            const decimals = number.includes(',') || number.includes('.') ? 1 : 0;
            return (Number(number.replace(',', '.')) * eased).toFixed(decimals).replace('.', ',');
          });
          if (progress < 1 && !reduced.matches) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: .6 });
      countObserver.observe(element);
    });
    if (!reduced.matches && 'IntersectionObserver' in window) {
      scope.querySelectorAll('[data-ia-hero]').forEach((hero) => {
        if (heroes.has(hero)) return;
        heroes.add(hero);
        hero.classList.add('ia-scroll-enhanced');
        const title = hero.querySelector('.ia-display');
        const italic = title?.querySelector('em');
        if (italic && !title.querySelector('.ia-title-first')) {
          const leading = document.createElement('span');
          leading.className = 'ia-title-first';
          while (title.firstChild && title.firstChild !== italic) leading.append(title.firstChild);
          title.prepend(leading);
        }
      });
      document.documentElement.classList.add('ia-motion');
      observer ||= new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) show(entry.target); }), { rootMargin: '0px 0px -5% 0px', threshold: .05 });
      scope.querySelectorAll('[data-ia-title],[data-ia-reveal]').forEach((element) => {
        if (element.classList.contains('ia-animate')) return;
        if (element.hasAttribute('data-ia-title')) words(element, 'ia-word');
        element.classList.add('ia-animate');
        animated.add(element);
        observer.observe(element);
      });
      scope.querySelectorAll('[data-ia-reading]').forEach((element) => { words(element, 'ia-read-word'); readings.add(element); });
    }
    requestUpdate();
  };
  init();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  let layoutWidth = window.innerWidth;
  let layoutHeight = window.innerHeight;
  window.addEventListener('resize', () => {
    const layoutChanged = window.innerWidth !== layoutWidth || Math.abs(window.innerHeight - layoutHeight) > 120;
    if (layoutChanged) {
      layoutWidth = window.innerWidth;
      layoutHeight = window.innerHeight;
      journeys.forEach(measureJourney);
      voiceScenes.forEach((scene) => scene.measure());
    }
    if (window.innerWidth > 1099) document.querySelectorAll('[data-ia-menu][open]').forEach((menu) => { menu.open = false; });
    requestUpdate();
  }, { passive: true });
  window.addEventListener('load', () => { journeys.forEach(measureJourney); requestUpdate(); }, { once: true });
  document.fonts?.ready.then(() => { journeys.forEach(measureJourney); requestUpdate(); });
  window.addEventListener('pageshow', requestUpdate);
  const closeDesktopMenus = (target) => {
    document.querySelectorAll('.ia-desktop-nav > .ia-nav-list > .ia-nav-item').forEach((item) => {
      if (item.contains(target)) return;
      const button = item.querySelector('[data-ia-nav-toggle][aria-expanded="true"]');
      if (button) button.click();
    });
  };
  document.addEventListener('click', (event) => closeDesktopMenus(event.target));
  document.addEventListener('focusin', (event) => closeDesktopMenus(event.target));
  document.addEventListener('click', (event) => {
    if (event.target.closest('#cart-icon-bubble')) document.querySelectorAll('[data-ia-menu][open]').forEach((menu) => { menu.open = false; });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  reduced.addEventListener('change', () => {
    journeys.forEach(measureJourney);
    voiceScenes.forEach((scene) => scene.measure());
    if (reduced.matches) {
      document.documentElement.classList.remove('ia-motion');
      heroes.forEach((hero) => hero.style.setProperty('--ia-scroll', '0'));
    } else init();
    requestUpdate();
  });
})();



